import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Sparkles } from 'lucide-react';
import { changelogApi, type ChangelogStatus } from '../../api/changelogApi';
import { haptic } from '../../telegram';
import { formatChangelogDate } from './changelogFormat';

// Список версий «Что нового». Каждая запись — заголовок + версия с датой;
// сам список изменений открывается на отдельной странице (ChangelogDetail).
export function Changelog() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['changelog'], queryFn: () => changelogApi.getStatus() });
  const entries = data?.entries ?? [];
  const notifyDisabled = data?.notifyDisabled ?? false;

  const preferenceMutation = useMutation({
    mutationFn: (next: boolean) => changelogApi.setNotifyDisabled(next),
    onSuccess: (res) => {
      qc.setQueryData<ChangelogStatus | undefined>(['changelog'], (prev) =>
        prev ? { ...prev, notifyDisabled: res.notifyDisabled } : prev
      );
    },
  });

  const toggle = () => {
    haptic('light');
    preferenceMutation.mutate(!notifyDisabled);
  };

  const openEntry = (version: string) => {
    haptic('light');
    nav(`/settings/changelog/${encodeURIComponent(version)}`);
  };

  return (
    <div className="rf-detail">
      <div className="rf-topbar">
        <button className="rf-back" onClick={() => nav(-1)}>
          <ChevronLeft /> Назад
        </button>
        <div className="rf-topbar-title">Что нового</div>
      </div>

      <div className="rf-detail-scroll">
        <label
          className="rf-card"
          style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 14, marginBottom: 12, cursor: 'pointer' }}
        >
          <input
            type="checkbox"
            checked={notifyDisabled}
            onChange={toggle}
            style={{ width: 18, height: 18, flexShrink: 0 }}
          />
          <span style={{ fontSize: 13, color: 'var(--rf-text-2, #96a0b5)' }}>
            Больше не показывать уведомление об обновлениях
          </span>
        </label>

        {isLoading && <div className="rf-info">Загрузка…</div>}
        {!isLoading && !entries.length && (
          <div className="rf-info"><Sparkles /><div>История версий пока пуста.</div></div>
        )}
        {!isLoading && entries.map((e) => (
          <button
            key={e.version}
            type="button"
            onClick={() => openEntry(e.version)}
            className="rf-card"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              width: '100%',
              padding: 16,
              marginBottom: 10,
              textAlign: 'left',
              border: 'none',
              cursor: 'pointer',
              font: 'inherit',
              color: 'inherit',
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4, color: 'var(--rf-text, #e8ecf4)' }}>
                {e.title}
              </div>
              <div style={{ fontSize: 12, color: 'var(--rf-text-2, #96a0b5)' }}>
                Версия от {formatChangelogDate(e.date)}
              </div>
            </div>
            <ChevronRight size={18} style={{ flexShrink: 0, color: 'var(--rf-text-2, #96a0b5)' }} />
          </button>
        ))}
      </div>
    </div>
  );
}

export default Changelog;
